import React, {useState, useCallback, useEffect, useMemo} from "react";
import Banner from "../assets/images/banners/EnterToWin.jpg";
import imageMob from "../assets/images/banners/EnterToWin-mob.jpg";
import { useOutletContext, useNavigate, useSearchParams } from "react-router-dom";
import parse from "html-react-parser";
import { handleApiCall } from "../apis";
import { endpoints } from "../utils/endpoints";
import { useLanguage } from "../context/LanguageContext";
import he from "he";

const buildMediaUrl = (path = "") => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = (process.env.REACT_APP_API || "").replace(/\/+$/, "");
  if (!base) return path;
  const normalizedBase = base.replace(/\/v1$/i, "");
  return `${normalizedBase}/${path.replace(/^\/+/, "")}`;
};

function EnterToWinSurvey() {
  const { setBannerData } = useOutletContext();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [competitionData, setCompetitionData] = useState(null);
  const [formQuestions, setFormQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  
  // Get form ID from URL params
  const formId = searchParams.get("formId");
  const competition_id = searchParams.get("competition_id");

  // const fetchCompetitionData = useCallback(async () => {
  //   setLoading(true);
  //   try {
  //     const response = await handleApiCall("get", endpoints.saudiaCompetitionList);
  //     if (
  //       response?.data?.code === 200 ||
  //       response?.data?.code === "200" ||
  //       response?.data?.success === true
  //     ) {
  //       const data = response?.data?.data;
  //       // Handle different response structures
  //       let normalized = null;
  //       if (Array.isArray(data?.competititon) && data.competititon.length > 0) {
  //         normalized = data.competititon[0];
  //       } else if (Array.isArray(data?.competition) && data.competition.length > 0) {
  //         normalized = data.competition[0];
  //       } else if (Array.isArray(data?.data) && data.data.length > 0) {
  //         normalized = data.data[0];
  //       } else if (Array.isArray(data) && data.length > 0) {
  //         normalized = data[0];
  //       } else if (data && typeof data === 'object') {
  //         normalized = data;
  //       }
  //       setCompetitionData(normalized);
  //     } else {
  //       setCompetitionData(null);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching competition data:", error);
  //     setCompetitionData(null);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, []);

  const isCompetitionEnded = useMemo(() => {
    if (!competitionData?.end_date) return false;
      const now = new Date();
      const endDate = new Date(competitionData.end_date);
  
      return now > endDate;
    }, [competitionData]);

  const fetchCompetitionData = useCallback(async () => {
  if (!competition_id) return;

  setLoading(true);
  try {
    const response = await handleApiCall(
      "get",
      `${endpoints.saudiaCompetitionDetails}/${competition_id}`
    );

    if (
      response?.data?.code === 200 ||
      response?.data?.code === "200" ||
      response?.data?.success === true
    ) {
      setCompetitionData(response?.data?.data?.competition);
    } else {
      setCompetitionData(null);
    }
  } catch (error) {
    console.error("Details fetch error:", error);
    setCompetitionData(null);
  } finally {
    setLoading(false);
  }
}, [competition_id]);


  const fetchFormQuestions = useCallback(async () => {
    setQuestionsLoading(true);
    try {
      const response = await handleApiCall("get", endpoints.formMasterList);
      if (
        response?.data?.code === 200 ||
        response?.data?.code === "200" ||
        response?.data?.success === true
      ) {
        const data = response?.data?.data;
        // Handle different response structures
        let normalized = [];
        if (Array.isArray(data?.data)) {
          normalized = data.data;
        } else if (Array.isArray(data)) {
          normalized = data;
        }
        // Filter only active questions (status === 1)
        setFormQuestions(normalized.filter(q => q.status === 1));
      } else {
        setFormQuestions([]);
      }
    } catch (error) {
      console.error("Error fetching form questions:", error);
      setFormQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompetitionData();
    fetchFormQuestions();
  }, [fetchCompetitionData, fetchFormQuestions]);

  // Get title from API
  const title = useMemo(() => {
    if (competitionData) {
      const titleEn = competitionData?.title_en || competitionData?.title_english;
      const titleAr = competitionData?.title_ar || competitionData?.title_arabic;
      return language === "AR" ? (titleAr || titleEn || "But wait... there's more") : (titleEn || titleAr || "But wait... there's more");
    }
    return "But wait... there's more";
  }, [competitionData, language]);

  const secondTitle = useMemo(() => {
      if (competitionData) {
      const titleEn = competitionData?.second_page_title_en || competitionData?.second_page_title_english;
      const titleAr = competitionData?.second_page_title_ar || competitionData?.second_page_title_arabic;
      return language === "AR" ? (titleAr || titleEn || "But wait... there's more") : (titleEn || titleAr || "But wait... there's more");
    }
    return "But wait... there's more";
    }, [competitionData, language]);

  // Get description from API
  const description = useMemo(() => {
    if (competitionData) {
      const desc = language === "AR"
        ? (competitionData?.second_page_description_ar || 
           competitionData?.second_page_description_arabic ||
           competitionData?.second_page_description_en ||
           competitionData?.second_page_description_english ||
           "")
        : (competitionData?.second_page_description_en || 
           competitionData?.second_page_description_english ||
           competitionData?.second_page_description_ar ||
           competitionData?.second_page_description_arabic ||
           "");
      return desc ? he.decode(desc) : "";
    }
    return "";
  }, [competitionData, language]);

  // Get banner images from API or fallback
  const desktopBanner = useMemo(() => {
    if (competitionData?.desktop_banner || competitionData?.desktop_image) {
      return buildMediaUrl(competitionData.desktop_banner || competitionData.desktop_image);
    }
    //return Banner;
  }, [competitionData]);

  const mobileBanner = useMemo(() => {
    if (competitionData?.mobile_banner || competitionData?.mobile_image) {
      return buildMediaUrl(competitionData.mobile_banner || competitionData.mobile_image);
    }
    //return imageMob;
  }, [competitionData]);

  useEffect(() => {
    setBannerData({
      image: desktopBanner,
      MobileBanner: mobileBanner,
      title: title,
      subtitle: "",
      skew: false,
      bannershow: true,
      titleClass: "text-[30px] md:text-[4rem] leading-tight text-white font-SaudiaBold !hidden"
    });
  }, [setBannerData, desktopBanner, mobileBanner, title]);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleChange = (e, questionId, optionId = null) => {
    const { value, type, checked } = e.target;
    
    if (type === "checkbox") {
      // For checkbox type, maintain an array of selected option IDs
      const currentValues = formData[questionId] || [];
      if (checked) {
        setFormData({ ...formData, [questionId]: [...currentValues, optionId] });
      } else {
        setFormData({ ...formData, [questionId]: currentValues.filter(id => id !== optionId) });
      }
    } else {
      // For single_selection (dropdown), store the selected option ID
      setFormData({ ...formData, [questionId]: value });
    }
    // Clear error when user makes changes
    if (submitError) {
      setSubmitError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formId) {
      setSubmitError(language === "AR" ? "معرف النموذج غير متوفر" : "Form ID not available");
      return;
    }

    // Map formData to API payload format
    const formResponses = formQuestions
      .filter(question => {
        const response = formData[question._id];
        // Include question if it has a response (not empty)
        if (question.type === "checkbox") {
          return Array.isArray(response) && response.length > 0;
        } else {
          return response && response !== "";
        }
      })
      .map(question => {
        const response = formData[question._id];
        if (question.type === "checkbox") {
          // For checkbox, option_ids is an array
          return {
            question_id: question._id,
            option_ids: Array.isArray(response) ? response : []
          };
        } else {
          // For single_selection, option_ids is an array with single value
          return {
            question_id: question._id,
            option_ids: response ? [response] : []
          };
        }
      });

    // if (formResponses.length === 0) {
    //   // setSubmitError(language === "AR" ? "يرجى الإجابة على سؤال واحد على الأقل" : "Please answer at least one question");
    //   setSubmitError(language === "AR" ? "يرجى الإجابة على سؤال واحد على الأقل" : "Please answer all question");

    //   return;
    // }
    const totalQuestions = formQuestions.length;
    const answeredQuestions = formResponses.length;

    if (answeredQuestions !== totalQuestions) {
      setSubmitError(
        language === "AR"
          ? "يرجى الإجابة على جميع الأسئلة"
          : "Please answer all questions"
      );
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    try {
      const payload = {
        competition_id : competition_id,
        enterToWinForm: formId,
        form_responses: formResponses
      };

      const response = await handleApiCall("post", endpoints.storeUserForm, payload);
      
      if (
        response?.data?.code === 200 ||
        response?.data?.code === "200" ||
        response?.data?.success === true
      ) {
        // Redirect to thanks page
        // navigate("/thanks");
        navigate(`/thanks?competition_id=${competition_id}`);

      } else {
        setSubmitError(response?.data?.message || (language === "AR" ? "حدث خطأ أثناء الإرسال" : "An error occurred while submitting"));
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitError(error?.response?.data?.message || (language === "AR" ? "حدث خطأ أثناء الإرسال" : "An error occurred while submitting"));
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question) => {
    const questionText = language === "AR" 
      ? (question.question_ar || question.question_en) 
      : (question.question_en || question.question_ar);
    
    const placeholderText = language === "AR" 
      ? "الرجاء قم بالاختيار" 
      : "Please make a selection";

    if (question.type === "single_selection") {
      return (
        <div key={question._id} className="block mb-6">
          <label className="text-sm font-medium">{questionText}</label>
          <select
            name={question._id}
            value={formData[question._id] || ""}
            className="mt-1 w-full p-2.5 rounded bg-transparent border font-SaudiaRegular text-sm h-10 border-white focus:outline-none placeholder:text-white/30 [&_option]:!text-black"
            onChange={(e) => handleChange(e, question._id)}
          >
            <option value="">{placeholderText}</option>
            {question.options?.map((option) => {
              const optionText = language === "AR" 
                ? (option.option_ar || option.option_en) 
                : (option.option_en || option.option_ar);
              return (
                <option key={option._id} value={option._id}>
                  {optionText}
                </option>
              );
            })}
          </select>
        </div>
      );
    } else if (question.type === "checkbox") {
      return (
        <div key={question._id} className="block mb-6">
          <label className="text-sm font-medium">{questionText}</label>
          <div className="grid grid-cols-12">
            <ul className="grid grid-cols-1 md:grid-cols-2 [&_label]:flex [&_label]:items-center [&_label]:gap-x-3 col-span-12 md:col-span-5 gap-y-0 md:gap-y-4 mt-4">
              {question.options?.map((option) => {
                const optionText = language === "AR" 
                  ? (option.option_ar || option.option_en) 
                  : (option.option_en || option.option_ar);
                const isChecked = (formData[question._id] || []).includes(option._id);
                return (
                  <li key={option._id}>
                    <label>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => handleChange(e, question._id, option._id)}
                      />
                      {optionText}
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      );
    }
    return null;
  };
  return (
    <>
      <div className="container max-w-8xl mx-auto px-2 py-8 md:py-0 md:px-8 2xl:px-32 grid md:grid-cols-12">
        <div className="mb-10 md:mb-20 gap-x-16 col-span-8 text-white">
          {loading ? (
            <p className="text-white/90 mb-10 text-lg font-SaudiaLight">
              {language === "AR" ? "جاري التحميل..." : "Loading..."}
            </p>
          ) : (
            <>
              <h2 className="text-3xl md:text-[2.625rem] font-SaudiaRegular mb-4 md:my-10">{secondTitle}</h2>
              {description ? (
                // <div className="text-white/90 mb-10 text-lg font-SaudiaLight">
                //   {parse(description)}
                // </div>
                <div
                  className="
                    text-white/90 mb-10 text-lg font-SaudiaLight
                    prose prose-invert max-w-none
                    [&_p]:mb-4
                    [&_p:empty]:h-4
                    [&_p:empty]:block
                    [&_br]:block
                  "
                >
                  {parse(description)}
                </div>
              ) : (
                <p className="text-white/90 mb-10 text-lg font-SaudiaLight">
                  {language === "AR" ? "لا توجد بيانات متاحة حالياً." : "No data available at the moment."}
                </p>
              )}
            </>
          )}
          <form onSubmit={handleSubmit} className="[&_label]:font-SaudiaRegular [&_label]:text-[17px] [&_label]:mb-1 [&_label]:flex">
            {questionsLoading ? (
              <p className="text-white/90 mb-10 text-lg font-SaudiaLight">
                {language === "AR" ? "جاري تحميل الأسئلة..." : "Loading questions..."}
              </p>
            ) : formQuestions.length > 0 ? (
              formQuestions.map((question) => renderQuestion(question))
            ) : (
              <p className="text-white/90 mb-10 text-lg font-SaudiaLight">
                {language === "AR" ? "لا توجد أسئلة متاحة حالياً." : "No questions available at the moment."}
              </p>
            )}
            {submitError && (
              <p className="text-red-500 text-sm mb-4 mt-4">{submitError}</p>
            )}
            <div className="flex justify-start items-start mt-8">
              {/* <button
                type="submit"
                disabled={submitting}
                className="bg-black text-white py-2 rounded-lg hover:bg-gray-950 transition px-16 text-lg font-SaudiaRegular disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting
                  ? (language === "AR" ? "جاري الإرسال..." : "Submitting...")
                  : (language === "AR" ? "اشترك الآن" : "Submit")
                }

              </button> */}
              <button
                type="submit"
                disabled={submitting || isCompetitionEnded}
                className="bg-black text-white py-2 rounded-lg hover:bg-gray-950 transition px-16 text-lg font-SaudiaRegular disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCompetitionEnded
                  ? (language === "AR" ? "المسابقة مغلقة" : "Competition Closed")
                  : submitting
                  ? (language === "AR" ? "جاري الإرسال..." : "Submitting...")
                  : (language === "AR" ? "اشترك الآن" : "Submit")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default EnterToWinSurvey;
